import React, { useEffect, useMemo, useState } from 'react'

import AdminLayout from '../components/AdminLayout'

import './dashboard.css'

/** Bar chart series item (penalty analytics). */
type PenaltyChartRow = { label: string; value: number; stpId: string }

const ALARM_TABLE_LIMIT = 5

/** Parsed STP alarm row from `/api/stp`. Recent Alarms uses fixed columns below. */
type StpAlarmRecord = Record<string, unknown>

/** Recent Alarms: fixed columns matching design (exact headings + API key fallbacks). */
type AlarmColumnDef = {
  id: string
  heading: string
  apiKeys: readonly string[]
}

const RECENT_ALARM_TABLE_COLUMNS: readonly AlarmColumnDef[] = [
  {
    id: 'stp_id',
    heading: 'STP ID',
    apiKeys: ['stp_id', 'stpId', 'STP_ID'],
  },
  {
    id: 'stp_vendor_name',
    heading: 'STP Vendor Name',
    apiKeys: [
      'stp_vendor_name',
      'stpVendorName',
      'vendor_name',
      'vendorName',
      'stp_vendor',
      'stpVendor',
    ],
  },
  {
    id: 'stp_contract_number',
    heading: 'STP Contract Number',
    apiKeys: [
      'stp_contract_number',
      'stpContractNumber',
      'contract_number',
      'contractNumber',
      'contract_no',
      'contractNo',
    ],
  },
  {
    id: 'stp_contract_name',
    heading: 'STP Contract Name',
    apiKeys: [
      'stp_contract_name',
      'stpContractName',
      'contract_name',
      'contractName',
    ],
  },
  {
    id: 'stp_contract_date_start',
    heading: 'STP Contract Date Start',
    apiKeys: [
      'stp_contract_date_start',
      'stpContractDateStart',
      'contract_date_start',
      'contractDateStart',
      'start_date',
      'startDate',
      'contract_start',
      'contractStart',
    ],
  },
  {
    id: 'stp_contract_date_end',
    heading: 'STP Contract Date End',
    apiKeys: [
      'stp_contract_date_end',
      'stpContractDateEnd',
      'contract_date_end',
      'contractDateEnd',
      'end_date',
      'endDate',
      'contract_end',
      'contractEnd',
    ],
  },
  {
    id: 'stp_location',
    heading: 'STP Location',
    apiKeys: ['stp_location', 'stpLocation', 'location', 'city_state', 'cityState', 'address'],
  },
]

const RECENT_ALARM_COL_SPAN = 1 + RECENT_ALARM_TABLE_COLUMNS.length

/** Risk breakdown row values + bar segment proportions (weights 100 : 75 : 50 : 25). */
const RISK_BAND_STATIC: readonly {
  label: string
  color: string
  segClass: string
  displayValue: string
  barWeight: number
}[] = [
  {
    label: 'Critical',
    color: '#dc2626',
    segClass: 'dashboard-risk-seg--c',
    displayValue: '100',
    barWeight: 100,
  },
  {
    label: 'High',
    color: '#f97316',
    segClass: 'dashboard-risk-seg--h',
    displayValue: '75',
    barWeight: 75,
  },
  {
    label: 'Medium',
    color: '#f3bb40',
    segClass: 'dashboard-risk-seg--m',
    displayValue: '50',
    barWeight: 50,
  },
  {
    label: 'Low',
    color: '#16a34a',
    segClass: 'dashboard-risk-seg--l',
    displayValue: '25',
    barWeight: 25,
  },
]

function extractList(json: unknown): unknown[] {
  if (Array.isArray(json)) return json
  const data = (json as { data?: unknown })?.data
  if (Array.isArray(data)) return data
  return []
}

function parseTotalPenalty(rec: Record<string, unknown>): number {
  const raw = rec.total_penalty ?? rec.totalPenalty
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  if (typeof raw === 'string') {
    const n = Number(raw)
    if (Number.isFinite(n)) return n
  }
  return 0
}

function pickStpDisplayName(rec: Record<string, unknown>): string | null {
  const raw =
    rec.stp_name ?? rec.stpName ?? rec.name ?? rec.plant_name ?? rec.plantName
  if (raw == null || raw === '') return null
  return String(raw)
}

function pickStpId(rec: Record<string, unknown>): string | null {
  const raw = rec.stp_id ?? rec.stpId
  if (raw == null || raw === '') return null
  const s = String(raw).trim()
  return s === '' ? null : s
}

/** X-axis label: plant name with API `stp_id` appended when both exist. */
function buildStpAxisLabel(name: string | null, stpId: string | null): string | null {
  if (name && stpId) return `${name} (${stpId})`
  if (stpId) return stpId
  if (name) return name
  return null
}

/**
 * One bar per unique `stp_id`. Duplicate API rows for the same STP are merged
 * (keeps max total_penalty so repeated rows do not stack). Rows without stp_id
 * dedupe by axis label.
 */
function dedupePenaltyChartRows(rows: PenaltyChartRow[]): PenaltyChartRow[] {
  const map = new Map<string, PenaltyChartRow>()
  for (const row of rows) {
    const key = row.stpId
      ? `id:${row.stpId}`
      : `lbl:${row.label}`
    const prev = map.get(key)
    if (!prev) {
      map.set(key, row)
    } else {
      map.set(key, {
        ...prev,
        value: Math.max(prev.value, row.value),
      })
    }
  }
  return Array.from(map.values())
}

/**
 * Aggregated risk score from STP analytics (`REACT_APP_API_STP_ANALYTICS`).
 * Checks root, `data`, and `summary` objects for `penalty_score` / `penaltyScore`.
 */
function parsePenaltyScoreFromAnalytics(json: unknown): number | null {
  const pick = (rec: Record<string, unknown>): number | null => {
    const raw = rec.penalty_score ?? rec.penaltyScore
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw
    if (typeof raw === 'string') {
      const n = Number(raw.trim())
      if (Number.isFinite(n)) return n
    }
    return null
  }

  if (!json || typeof json !== 'object' || Array.isArray(json)) return null
  const root = json as Record<string, unknown>

  const fromRoot = pick(root)
  if (fromRoot !== null) return fromRoot

  const data = root.data
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const fromData = pick(data as Record<string, unknown>)
    if (fromData !== null) return fromData
  }

  const summary = root.summary
  if (summary && typeof summary === 'object' && !Array.isArray(summary)) {
    const fromSummary = pick(summary as Record<string, unknown>)
    if (fromSummary !== null) return fromSummary
  }

  return null
}

/** STP Analytics chart: one row per STP, bar height = total_penalty; `stp_id` from API is shown on the axis. */
function parsePenaltyChartRows(json: unknown): PenaltyChartRow[] {
  const list = extractList(json)
  const rows: PenaltyChartRow[] = []
  for (const item of list) {
    if (!item || typeof item !== 'object') continue
    const rec = item as Record<string, unknown>
    const name = pickStpDisplayName(rec)
    const stpId = pickStpId(rec)
    const label = buildStpAxisLabel(name, stpId)
    if (!label) continue
    rows.push({
      label,
      value: parseTotalPenalty(rec),
      stpId: stpId ?? '',
    })
  }
  const unique = dedupePenaltyChartRows(rows)
  return unique.sort(
    (a, b) =>
      b.value - a.value ||
      a.stpId.localeCompare(b.stpId, undefined, { numeric: true }) ||
      a.label.localeCompare(b.label)
  )
}

/** Fixed domain for STP penalty chart Y-axis (bar height scaled to this max). */
const STP_CHART_Y_MAX = 10000

/** Y-axis ticks: 10000 down to 0, step 1000. */
function fixedYAxisTicks0To10000(): number[] {
  const ticks: number[] = []
  for (let v = STP_CHART_Y_MAX; v >= 0; v -= 1000) {
    ticks.push(v)
  }
  return ticks
}

/**
 * Penalty analytics chart URL (`/api/penalty`). Same-origin relative paths use the
 * dev proxy (craco `/api` → backend). Full http(s) URLs require CORS on the API.
 */
function buildPenaltyChartRequestUrl(district: string): string {
  const pathOrUrl =
    process.env.REACT_APP_API_STP_ANALYTICS ||
    '/api/penalty'

  let href: string
  if (pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://')) {
    href = pathOrUrl
  } else {
    href = `${window.location.origin}${
      pathOrUrl.startsWith('/') ? '' : '/'
    }${pathOrUrl}`
  }

  const u = new URL(href)
  if (district) u.searchParams.set('district', district)
  return u.toString()
}

/**
 * Builds URL for `/api/stp` (same-origin + proxy, or absolute URL in production).
 */
function buildStpListRequestUrl(): string {
  const pathOrUrl = process.env.REACT_APP_API_STP || '/api/stp'

  if (pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://')) {
    return pathOrUrl
  }
  return `${window.location.origin}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`
}

function parseStpAlarmRecords(json: unknown): StpAlarmRecord[] {
  const list = extractList(json)
  const rows: StpAlarmRecord[] = []
  for (const item of list) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue
    rows.push({ ...(item as StpAlarmRecord) })
  }
  return rows
}

const RECENCY_FIELD_KEYS = [
  'updated_at',
  'updatedAt',
  'modified_at',
  'modifiedAt',
  'created_at',
  'createdAt',
  'inserted_at',
  'insertedAt',
  'timestamp',
  'recorded_at',
  'recordedAt',
  'alarm_time',
  'alarmTime',
  'date_time',
  'dateTime',
  'stp_contract_date_end',
  'stpContractDateEnd',
  'contract_date_end',
  'contractDateEnd',
  'end_date',
  'endDate',
]

function extractRecencyTs(rec: StpAlarmRecord): number | null {
  for (const k of RECENCY_FIELD_KEYS) {
    const v = rec[k]
    if (typeof v === 'number' && Number.isFinite(v)) {
      return v > 1e12 ? v : v * 1000
    }
    if (typeof v === 'string') {
      const t = Date.parse(v)
      if (!Number.isNaN(t)) return t
      const num = Number(v)
      if (Number.isFinite(num) && num > 946684800000) return num
    }
  }
  return null
}

/** Newest-first when timestamps exist; otherwise keeps API order. */
function sortStpAlarmsRecentFirst(records: StpAlarmRecord[]): StpAlarmRecord[] {
  return [...records].sort((a, b) => {
    const tb = extractRecencyTs(b)
    const ta = extractRecencyTs(a)
    if (ta !== null || tb !== null) {
      return (tb ?? 0) - (ta ?? 0)
    }
    return 0
  })
}

function pickAlarmField(row: StpAlarmRecord, apiKeys: readonly string[]): unknown {
  for (const k of apiKeys) {
    if (!Object.prototype.hasOwnProperty.call(row, k)) continue
    const v = row[k]
    if (v === null || v === undefined) continue
    if (typeof v === 'string' && v.trim() === '') continue
    return v
  }
  return undefined
}

function isStatusAlarmColumn(key: string): boolean {
  const k = key.toLowerCase()
  return (
    k === 'status' ||
    k.endsWith('_status') ||
    k === 'contractstatus' ||
    k === 'contract_status' ||
    k === 'state'
  )
}

function isAlarmContractDateColumn(columnKey: string): boolean {
  return (
    columnKey === 'stp_contract_date_start' || columnKey === 'stp_contract_date_end'
  )
}

/** Recent Alarms contract dates → `yyyy-mm-dd` (UTC calendar day for ISO timestamps). */
function formatAlarmDateYyyyMmDd(value: unknown): string | null {
  if (value == null || value === '') return null
  if (typeof value === 'number' && Number.isFinite(value)) {
    const ms = value > 1e12 ? value : value * 1000
    const d = new Date(ms)
    if (Number.isNaN(d.getTime())) return null
    const y = d.getUTCFullYear()
    const m = String(d.getUTCMonth() + 1).padStart(2, '0')
    const day = String(d.getUTCDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }
  const s = String(value).trim()
  if (!s) return null
  const isoPrefix = /^(\d{4})-(\d{2})-(\d{2})/.exec(s)
  if (isoPrefix) return `${isoPrefix[1]}-${isoPrefix[2]}-${isoPrefix[3]}`
  const parsed = Date.parse(s)
  if (!Number.isNaN(parsed)) {
    const d = new Date(parsed)
    const y = d.getUTCFullYear()
    const m = String(d.getUTCMonth() + 1).padStart(2, '0')
    const day = String(d.getUTCDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }
  return null
}

function formatAlarmPrimitive(value: unknown): string {
  if (value == null || value === '') return ''
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value).trim()
}

function alarmCellNode(columnKey: string, value: unknown): React.ReactNode {
  if (value !== null && typeof value === 'object') {
    if (Array.isArray(value)) {
      if (value.length === 0) return '—'
      return JSON.stringify(value)
    }
    const o = value as Record<string, unknown>
    if (Object.keys(o).length === 0) return '—'
    return JSON.stringify(value)
  }

  const s = formatAlarmPrimitive(value)
  if (!s) return '—'

  if (isAlarmContractDateColumn(columnKey)) {
    const ymd = formatAlarmDateYyyyMmDd(value)
    return ymd ?? s
  }

  if (isStatusAlarmColumn(columnKey)) {
    return <span className={alarmStatusPillClass(s)}>{s}</span>
  }

  return s
}

function alarmStatusPillClass(status: string): string {
  const s = status.toLowerCase()
  if (s === 'active') return 'admin-pill'
  if (s.includes('pend') || s.includes('review')) return 'admin-pill admin-pill--muted'
  return 'admin-pill admin-pill--neutral'
}

function alarmRowStableKey(row: StpAlarmRecord, index: number): string {
  const raw = row.stp_id ?? row.stpId ?? row.id ?? row._id
  if (raw != null && String(raw).trim() !== '') return `${String(raw)}-${index}`
  return `alarm-dynamic-${index}`
}

const Dashboard = () => {
  const [district, setDistrict] = useState('')
  const [penaltyRows, setPenaltyRows] = useState<PenaltyChartRow[]>([])
  const [penaltyScore, setPenaltyScore] = useState<number | null>(null)
  const [penaltyLoading, setPenaltyLoading] = useState(true)
  const [penaltyError, setPenaltyError] = useState<string | null>(null)

  const [alarmRows, setAlarmRows] = useState<StpAlarmRecord[]>([])
  const [alarmLoading, setAlarmLoading] = useState(true)
  const [alarmError, setAlarmError] = useState<string | null>(null)

  useEffect(() => {
    const url = buildPenaltyChartRequestUrl(district)

    let cancelled = false

    setPenaltyLoading(true)
    setPenaltyError(null)
    setPenaltyScore(null)

    fetch(url)
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`Penalty chart request failed (${res.status})`)
        }
        return res.json()
      })
      .then((json: unknown) => {
        if (cancelled) return
        setPenaltyScore(parsePenaltyScoreFromAnalytics(json))
        const rows = parsePenaltyChartRows(json)
        setPenaltyRows(rows)
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setPenaltyScore(null)
          const msg =
            e instanceof TypeError && e.message === 'Failed to fetch'
              ? 'Could not reach penalty API. Start the backend on port 5000 and keep npm dev proxy for /api (see craco.config.js).'
              : e instanceof Error
                ? e.message
                : 'Failed to load STP analytics.'
          setPenaltyError(msg)
        }
      })
      .finally(() => {
        if (!cancelled) setPenaltyLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [district])

  useEffect(() => {
    const url = buildStpListRequestUrl()
    let cancelled = false

    setAlarmLoading(true)
    setAlarmError(null)
    setAlarmRows([])

    fetch(url)
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`STP list request failed (${res.status})`)
        }
        return res.json()
      })
      .then((json: unknown) => {
        if (cancelled) return
        const records = parseStpAlarmRecords(json)
        const sorted = sortStpAlarmsRecentFirst(records)
        const top = sorted.slice(0, ALARM_TABLE_LIMIT)
        setAlarmRows(top)
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          const msg =
            e instanceof TypeError && e.message === 'Failed to fetch'
              ? 'Could not reach STP API. Start the backend on port 5000 and set REACT_APP_API_STP (see craco.config.js).'
              : e instanceof Error
                ? e.message
                : 'Failed to load STP list.'
          setAlarmError(msg)
        }
      })
      .finally(() => {
        if (!cancelled) setAlarmLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const { yTicks, barPercents } = useMemo(() => {
    const yTicks = fixedYAxisTicks0To10000()
    if (penaltyRows.length === 0) {
      return { yTicks, barPercents: [] as number[] }
    }
    const percents = penaltyRows.map((r) =>
      Math.min(100, (r.value / STP_CHART_Y_MAX) * 100)
    )
    return { yTicks, barPercents: percents }
  }, [penaltyRows])

  return (
    <AdminLayout title="Dashboard" activeNav="dashboard" pageTitle="Dashboard - Namami Gange">
      <div className="admin-card-grid">
        <div className="admin-stat-card">
          <h3>Active Sites</h3>
          <div className="admin-stat-value">34</div>
        </div>
        <div className="admin-stat-card">
          <h3>Green Plants</h3>
          <div className="admin-stat-value dashboard-kpi-accent dashboard-kpi-accent--green">
            32
          </div>
        </div>
        <div className="admin-stat-card">
          <h3>Critical Alerts</h3>
          <div className="admin-stat-value dashboard-kpi-accent dashboard-kpi-accent--red">
            3
          </div>
        </div>
        <div className="admin-stat-card">
          <h3>Total Penalties</h3>
          <div className="admin-stat-value dashboard-kpi-accent dashboard-kpi-accent--gold">
            ₹50,000
          </div>
          <div className="admin-stat-sub">8 violations</div>
        </div>
      </div>

      <div className="dashboard-row-mid">
        <div className="admin-panel dashboard-chart">
          <div className="dashboard-chart-head">
            <h2>STP Analytics</h2>
            <select
              aria-label="District"
              value={district}
              onChange={(e) => setDistrict(e.target.value)}
            >
              <option value="">District</option>
              <option value="Haridwar">Haridwar</option>
              <option value="Varanasi">Varanasi</option>
            </select>
          </div>
          <div className="dashboard-chart-inner dashboard-chart-inner--stp-y10k">
            <div className="dashboard-chart-stp-body">
              <div className="dashboard-y-axis dashboard-y-axis--step1k dashboard-y-axis--stp-grid">
                {yTicks.map((t, idx) => (
                  <div key={`${idx}-${t}`} className="dashboard-y-tick-row">
                    <span className="dashboard-y-tick-row__label">{t}</span>
                    <span className="dashboard-y-tick-row__mark" aria-hidden="true">
                      -
                    </span>
                  </div>
                ))}
              </div>
              <div className="dashboard-chart-stp-plot">
                <div className="dashboard-bars dashboard-bars--stp">
                  {penaltyLoading && (
                    <div className="dashboard-stp-overlay">Loading…</div>
                  )}
                  {!penaltyLoading && penaltyError && (
                    <div className="dashboard-stp-overlay dashboard-stp-overlay--error">
                      {penaltyError}
                    </div>
                  )}
                  {!penaltyLoading &&
                    !penaltyError &&
                    penaltyRows.length > 0 &&
                    penaltyRows.map((row, i) => (
                      <div
                        key={row.stpId ? `bar-${row.stpId}-${i}` : `bar-${i}`}
                        className="dashboard-bar dashboard-bar--stp"
                        style={{ height: `${barPercents[i] ?? 0}%` }}
                        title={
                          row.stpId
                            ? `stp_id ${row.stpId} · total_penalty ${row.value}`
                            : `${row.label}: total_penalty ${row.value}`
                        }
                      />
                    ))}
                  {!penaltyLoading && !penaltyError && penaltyRows.length === 0 && (
                    <div className="dashboard-stp-overlay">
                      No penalty data returned.
                    </div>
                  )}
                </div>
                <div className="dashboard-bar-labels dashboard-bar-labels--long">
                  {!penaltyLoading &&
                    !penaltyError &&
                    penaltyRows.map((row, idx) => (
                      <span key={row.stpId ? `lbl-${row.stpId}-${idx}` : `lbl-${idx}`}>
                        {row.label}
                      </span>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="admin-panel">
          <h2>Risk Score</h2>
          <div
            className="dashboard-risk-value"
            title={
              penaltyScore !== null
                ? `penalty_score from analytics`
                : penaltyError
                  ? penaltyError
                  : undefined
            }
          >
            {penaltyLoading
              ? '…'
              : penaltyScore !== null
                ? Number.isInteger(penaltyScore)
                  ? String(penaltyScore)
                  : penaltyScore.toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                    })
                : '—'}
          </div>
          <div className="dashboard-risk-bar">
            {RISK_BAND_STATIC.map((band) => (
              <div
                key={band.label}
                className={`dashboard-risk-seg ${band.segClass}`}
                style={{ flex: `${band.barWeight} 1 0%` }}
              />
            ))}
          </div>
          <div className="dashboard-risk-rows">
            {RISK_BAND_STATIC.map((band) => (
              <div key={band.label} className="dashboard-risk-row">
                <span>
                  <span
                    className="dashboard-risk-dot"
                    style={{ background: band.color }}
                  />
                  {band.label}
                </span>
                <strong>{band.displayValue}</strong>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="admin-panel">
        <h2>Recent Alarms</h2>
        <div className="admin-table-wrap dashboard-alarm-table-scroll">
          <table className="admin-table">
            {!alarmLoading && !alarmError && (
              <thead>
                <tr>
                  <th>S No.</th>
                  {RECENT_ALARM_TABLE_COLUMNS.map((col) => (
                    <th key={col.id}>{col.heading}</th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {alarmLoading && (
                <tr>
                  <td colSpan={RECENT_ALARM_COL_SPAN} className="dashboard-alarm-msg">
                    Loading…
                  </td>
                </tr>
              )}
              {!alarmLoading && alarmError && (
                <tr>
                  <td
                    colSpan={RECENT_ALARM_COL_SPAN}
                    className="dashboard-alarm-msg dashboard-alarm-msg--error"
                  >
                    {alarmError}
                  </td>
                </tr>
              )}
              {!alarmLoading && !alarmError && alarmRows.length === 0 && (
                <tr>
                  <td colSpan={RECENT_ALARM_COL_SPAN} className="dashboard-alarm-msg">
                    No STP records returned.
                  </td>
                </tr>
              )}
              {!alarmLoading &&
                !alarmError &&
                alarmRows.map((row, i) => (
                  <tr key={alarmRowStableKey(row, i)}>
                    <td>{i + 1}</td>
                    {RECENT_ALARM_TABLE_COLUMNS.map((col) => (
                      <td key={col.id}>
                        {alarmCellNode(col.id, pickAlarmField(row, col.apiKeys))}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  )
}

export default Dashboard
